import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useStorageQuota } from '../../hooks/useStorageQuota';
import { 
  Calendar, 
  Package, 
  HardDrive, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Edit2,
  Plus,
  Minus,
  ChevronDown,
  ChevronUp,
  X,
  RefreshCw,
  Loader2,
  Info,
  Sparkles,
  Lightbulb
} from 'lucide-react';
import { SUBSCRIPTION_PLANS, PRO_ADDONS, getPlanByTier } from '../../config/subscriptionPlans';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { validateUsername, sanitizeUsername } from '../../utils/usernameValidator';
import { checkUsernameAvailability } from '../../services/usernameService';

interface SelectedAddon {
  id: string;
  name: string;
  storageGB: number;
  price: number;
  quantity: number;
}

export default function RenewSubscription() {
  const { user } = useAuthContext();
  const { plan, tier } = useSubscription();
  const { usedGB, limitGB } = useStorageQuota();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if focus=storage query parameter exists
  const focusStorage = searchParams.get('focus') === 'storage';

  // Debug: Log tier value
  console.log('🔍 RenewSubscription - tier:', tier);
  console.log('🔍 RenewSubscription - user.subscriptionTier:', user?.subscriptionTier);
  console.log('🔍 RenewSubscription - focusStorage:', focusStorage);
  
  // Determine if user is free tier (includes expired users who need to upgrade)
  const isFreeUser = !tier || tier === 'free' || tier === 'expired' || tier === '' || 
                     !user?.subscriptionTier || user?.subscriptionTier === 'free' || user?.subscriptionTier === 'expired';
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string>(tier);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'semi_annual' | 'yearly'>('monthly');
  const [selectedPackageTier, setSelectedPackageTier] = useState<'pro' | 'pro_plus' | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
  const [addonStorageGB, setAddonStorageGB] = useState<number>(0);
  const [currentAddons, setCurrentAddons] = useState<any[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);
  const [showUrlAddonOptions, setShowUrlAddonOptions] = useState(false);
  const [showAddonOptions, setShowAddonOptions] = useState(focusStorage);
  const [showSummary, setShowSummary] = useState(false);
  
  // Phase 2: URL Add-on states
  const [hasUrlAddon, setHasUrlAddon] = useState(false);
  const [existingUrlAddonType, setExistingUrlAddonType] = useState<'path' | 'subdomain' | null>(null);
  const [existingUrlAddonUsername, setExistingUrlAddonUsername] = useState<string | null>(null);
  const [renewUrlAddon, setRenewUrlAddon] = useState(false);
  const [hasEverHadPaidSubscription, setHasEverHadPaidSubscription] = useState(false);
  const [urlAddonUsername, setUrlAddonUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState<any[]>([]);
  const [latestUserData, setLatestUserData] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchSubscriptionData = async () => {
      try {
        // Fetch user addons and latest subscription data
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        setCurrentAddons(userData?.addons || []);
        setLatestUserData(userData); // Store latest user data

        // Fetch subscription data from Firestore (get latest by updatedAt)
        const subscriptionsQuery = query(
          collection(db, 'subscriptions'),
          where('userId', '==', user.uid),
          orderBy('updatedAt', 'desc'),
          limit(1)
        );
        const subscriptionsSnap = await getDocs(subscriptionsQuery);
        
        if (!subscriptionsSnap.empty) {
          const subData = subscriptionsSnap.docs[0].data();
          console.log('📅 RenewSubscription - Subscription endDate:', subData.endDate?.toDate());
          console.log('📅 RenewSubscription - User subscriptionEndDate:', userData?.subscriptionEndDate?.toDate());
          setSubscriptionData(subData);
        } else {
          console.log('⚠️ RenewSubscription - No subscription found in subscriptions collection');
        }
        
        // Fetch subscription history from subscription_payments (approved payments)
        const paymentsHistoryQuery = query(
          collection(db, 'subscription_payments'),
          where('userId', '==', user.uid),
          where('status', '==', 'approved')
        );
        const paymentsHistorySnap = await getDocs(paymentsHistoryQuery);
        const paymentsHistory = paymentsHistorySnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          renewedAt: doc.data().approvedAt?.toDate() || doc.data().createdAt?.toDate(),
          amount: doc.data().amount,
          tier: doc.data().plan,
        }));
        console.log('📊 [DEBUG] subscription_payments (approved):', paymentsHistory.length, 'records');
        console.log('📊 [DEBUG] subscription_payments data:', paymentsHistory);

        // Also fetch from subscriptionHistory collection
        const historyQuery = query(
          collection(db, 'subscriptionHistory'),
          where('userId', '==', user.uid)
        );
        const historySnap = await getDocs(historyQuery);
        const history = historySnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          renewedAt: doc.data().renewedAt?.toDate() || doc.data().createdAt?.toDate(),
          oldEndDate: doc.data().oldEndDate?.toDate(),
          newEndDate: doc.data().newEndDate?.toDate(),
        }));
        console.log('📊 [DEBUG] subscriptionHistory:', history.length, 'records');
        console.log('📊 [DEBUG] subscriptionHistory data:', history);

        // Also fetch from paymentLogs (subscription renewals only, not first activations)
        const paymentLogsQuery = query(
          collection(db, 'paymentLogs'),
          where('userId', '==', user.uid),
          where('type', '==', 'subscription'),
          where('status', '==', 'completed')
        );
        const paymentLogsSnap = await getDocs(paymentLogsQuery);
        const paymentLogs = paymentLogsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          renewedAt: doc.data().timestamp?.toDate(),
          amount: doc.data().amount || 0,
          tier: doc.data().tier,
          newEndDate: doc.data().endDate?.toDate(),
        }));
        console.log('📊 [DEBUG] paymentLogs (subscription, completed):', paymentLogs.length, 'records');
        console.log('📊 [DEBUG] paymentLogs data:', paymentLogs);

        // Fetch URL addon renewals from addonPayments
        const urlAddonQuery = query(
          collection(db, 'addonPayments'),
          where('userId', '==', user.uid),
          where('status', '==', 'approved'),
          where('addonType', '==', 'url')
        );
        const urlAddonSnap = await getDocs(urlAddonQuery);
        const urlAddonHistory = urlAddonSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          renewedAt: doc.data().approvedAt?.toDate() || doc.data().createdAt?.toDate(),
          amount: doc.data().amount || 0,
          tier: 'URL Add-on',
          isUrlAddon: true,
        }));
        console.log('📊 [DEBUG] addonPayments (url, approved):', urlAddonHistory.length, 'records');
        console.log('📊 [DEBUG] addonPayments data:', urlAddonHistory);

        // Combine and sort by renewedAt
        // Filter to show only renewals (exclude first activations if needed)
        const allHistory = [...paymentsHistory, ...history, ...paymentLogs, ...urlAddonHistory]
          .filter(record => {
            // Exclude storage addons
            if (record.addonType === 'storage') return false;
            // Include all others (subscription renewals and URL addon renewals)
            return true;
          })
          .sort((a, b) => (b.renewedAt?.getTime() || 0) - (a.renewedAt?.getTime() || 0));
        
        console.log('📊 [DEBUG] Combined allHistory (after filter):', allHistory.length, 'records');
        console.log('📊 [DEBUG] Combined allHistory data:', allHistory);
        
        setSubscriptionHistory(allHistory);
        
        // Phase 2: Check if user has URL add-on and get existing username
        const urlAddonDoc = await getDoc(doc(db, 'urlAddons', user.uid));
        if (urlAddonDoc.exists() && urlAddonDoc.data().status === 'active') {
          setHasUrlAddon(true);
          const existingUsername = urlAddonDoc.data().username;
          const addonType = urlAddonDoc.data().type || 'subdomain';
          setExistingUrlAddonType(addonType);
          if (existingUsername) {
            setExistingUrlAddonUsername(existingUsername);
            setUrlAddonUsername(existingUsername);
          }
          // Auto-check renewal for existing URL addon
          setRenewUrlAddon(true);
        }
        
        // Phase 2: Check if user has ever had a paid subscription
        // Check omise_subscriptions collection for payment history
        const omiseSubsQuery = query(
          collection(db, 'omise_subscriptions'),
          where('userId', '==', user.uid)
        );
        const omiseSubsSnap = await getDocs(omiseSubsQuery);
        
        if (!omiseSubsSnap.empty) {
          // User has payment history
          setHasEverHadPaidSubscription(true);
        } else if (tier && tier !== 'free' && tier !== 'expired') {
          // User currently has paid tier (might be trial or first upgrade)
          setHasEverHadPaidSubscription(true);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [user, navigate]);

  // Refresh data when user comes back to this page (e.g., after renewal)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // Refetch data when page becomes visible
        const refetchData = async () => {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.data();
          setLatestUserData(userData);
        };
        refetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  // Auto-scroll to Storage Add-on section when focus=storage
  useEffect(() => {
    if (focusStorage && !loading) {
      setTimeout(() => {
        const storageSection = document.getElementById('storage-addon-section');
        if (storageSection) {
          storageSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [focusStorage, loading]);

  const formatDate = (date: any) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getDaysUntilExpiry = (expiryDate: any) => {
    if (!expiryDate) return null;
    const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
    const now = new Date();
    const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Calculate daysLeft from subscriptionData.endDate or latestUserData.subscriptionEndDate or trial
  const getExpiryDate = () => {
    if (subscriptionData?.endDate) return subscriptionData.endDate;
    // Use latestUserData instead of user from context to get fresh data
    if (latestUserData?.subscriptionEndDate) return latestUserData.subscriptionEndDate;
    if (user?.subscriptionEndDate) return user.subscriptionEndDate;
    // If free trial, calculate from trialStartedAt or createdAt
    const userData = latestUserData || user;
    if (userData?.subscriptionTier === 'free' || userData?.plan === 'free') {
      const startDate = userData?.trialStartedAt || userData?.createdAt;
      if (startDate) {
        const trialStart = startDate.toDate ? startDate.toDate() : new Date(startDate);
        return new Date(trialStart.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days trial
      }
    }
    return null;
  };
  
  const expiryDate = getExpiryDate();
  const daysLeft = expiryDate ? getDaysUntilExpiry(expiryDate) : null;

  const handleAddAddon = (addon: typeof PRO_ADDONS[0]) => {
    const existing = selectedAddons.find(a => a.id === addon.id);
    if (existing) {
      setSelectedAddons(selectedAddons.map(a => 
        a.id === addon.id ? { ...a, quantity: a.quantity + 1 } : a
      ));
    } else {
      setSelectedAddons([...selectedAddons, {
        id: addon.id,
        name: addon.name,
        storageGB: addon.storageGB,
        price: addon.price,
        quantity: 1
      }]);
    }
  };

  const handleRemoveAddon = (addonId: string) => {
    const existing = selectedAddons.find(a => a.id === addonId);
    if (existing && existing.quantity > 1) {
      setSelectedAddons(selectedAddons.map(a => 
        a.id === addonId ? { ...a, quantity: a.quantity - 1 } : a
      ));
    } else {
      setSelectedAddons(selectedAddons.filter(a => a.id !== addonId));
    }
  };

  const calculateTotal = () => {
    // Include plan price if:
    // 1. User has selected a different plan from their current tier, OR
    // 2. User is free/expired and has selected a paid plan
    const isUpgrading = selectedPlan !== tier || (isFreeUser && selectedPlan && selectedPlan !== 'free');
    const planPrice = isUpgrading ? (getPlanByTier(selectedPlan)?.price || 0) : 0;
    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + (addon.price * addon.quantity), 0);
    
    console.log('💰 [calculateTotal] selectedPlan:', selectedPlan, 'tier:', tier, 'isFreeUser:', isFreeUser, 'isUpgrading:', isUpgrading, 'planPrice:', planPrice);
    
    // Add URL addon price (for renewal)
    let urlAddonPrice = 0;
    const isYearly = selectedPlan.includes('yearly');
    const isSemiAnnual = selectedPlan.includes('semi_annual');
    const isProPlusYearly = selectedPlan === 'pro_plus_yearly';
    
    // Calculate URL addon renewal price
    if (renewUrlAddon && hasUrlAddon && existingUrlAddonType) {
      // Pro+ Yearly gets Subdomain free
      if (isProPlusYearly && existingUrlAddonType === 'subdomain') {
        urlAddonPrice = 0; // Free for Pro+ Yearly
      } else {
        // Calculate price based on type and billing cycle
        if (existingUrlAddonType === 'path') {
          urlAddonPrice = isYearly ? 490 : isSemiAnnual ? 245 : 49;
        } else if (existingUrlAddonType === 'subdomain') {
          urlAddonPrice = isYearly ? 990 : isSemiAnnual ? 495 : 99;
        }
      }
    }
    
    // Pro+ Yearly gets free Subdomain (for first-time buyers without URL addon)
    if (isProPlusYearly && !hasUrlAddon) {
      // Subdomain is free, included automatically
      urlAddonPrice = 0;
    }
    
    console.log('💰 [calculateTotal] urlAddonPrice:', urlAddonPrice, 'renewUrlAddon:', renewUrlAddon, 'hasUrlAddon:', hasUrlAddon, 'existingUrlAddonType:', existingUrlAddonType);
    
    // Add storage addon price
    let storageAddonPrice = 0;
    if (addonStorageGB > 0) {
      const units = addonStorageGB / 5;
      const rawPrice = units * 99;
      if (addonStorageGB === 5) {
        storageAddonPrice = 99;
      } else {
        storageAddonPrice = Math.floor(rawPrice / 10) * 10;
      }
    }
    
    const total = planPrice + addonsPrice + urlAddonPrice + storageAddonPrice;
    console.log('💰 [calculateTotal] TOTAL:', total);
    
    return total;
  };

  const calculateTotalStorage = () => {
    // Get plan storage in GB (videoStorage is stored in MB, so divide by 1024)
    const planStorage = (getPlanByTier(selectedPlan)?.features.storageGB.videoStorage || 0) / 1024;
    
    // Get current addons storage (permanent addons that user already has)
    const currentAddonsStorage = Array.isArray(currentAddons) 
      ? currentAddons.reduce((sum, addon) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
      : 0;
    
    // Get selected addons storage (new addons being added)
    const selectedAddonsStorage = selectedAddons.reduce((sum, addon) => sum + (addon.storageGB * addon.quantity), 0);
    
    // Total = plan storage + existing addons + new addons + new storage addon
    // Note: currentAddons are permanent (bought once, use forever)
    return planStorage + currentAddonsStorage + selectedAddonsStorage + addonStorageGB;
  };

  const handleQuickRenew = () => {
    // Prepare renewal data with current plan and all add-ons
    const addons = Array.isArray(currentAddons) ? currentAddons.map(addon => ({
      id: addon.id || `addon-${addon.storageGB}`,
      name: addon.name,
      storageGB: addon.storageGB,
      price: addon.price || 0,
      quantity: addon.quantity || 1
    })) : [];
    
    // Get plan data with fallback
    let planData = plan;
    if (!planData && tier) {
      planData = getPlanByTier(tier);
    }
    if (!planData) {
      planData = {
        tier: 'free',
        name: 'Free Trial (14 วัน)',
        price: 0,
        billing_cycle: 'monthly',
        features: {
          storage_gb: 20,
          unlimited_students: true,
          unlimited_courses: true,
          dashboard_analytics: false,
          seo_optimization: false,
          sale_page_builder: false
        }
      };
    }
    
    const checkoutData = {
      plan: tier || 'free',
      planName: planData?.name || 'Free Trial (14 วัน)',
      planPrice: planData?.price || 0,
      addons: addons,
      total: getCurrentPackageTotal(),
      expiryDate: formatDate(expiryDate),
      isRenewal: true,
      isQuickRenew: true
    };
    
    console.log('💾 [QuickRenew] Saving to localStorage:', checkoutData);
    localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutData));
    
    const tierParam = tier || 'free';
    console.log('🚀 [QuickRenew] Navigating to checkout with tier:', tierParam);
    navigate(`/subscription/checkout-new?tier=${tierParam}`);
  };

  const handleUpgrade = () => {
    setShowUpgradeOptions(true);
    setShowUrlAddonOptions(false);
    const scrollTarget = document.getElementById('upgrade-section');
    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getAvailablePlans = () => {
    const baseTier = tier.replace(/_monthly|_semi_annual|_yearly/, '');
    const tierHierarchy = ['free', 'pro', 'pro_plus'];
    const currentTierIndex = tierHierarchy.indexOf(baseTier);
    
    return SUBSCRIPTION_PLANS.filter(p => {
      if (p.tier === 'free') return false;
      const planBaseTier = p.tier.replace(/_monthly|_semi_annual|_yearly/, '');
      const planTierIndex = tierHierarchy.indexOf(planBaseTier);
      // Allow current tier and higher tiers (>= instead of >)
      return planTierIndex >= currentTierIndex;
    });
  };

  const getBillingCycleOptions = (baseTier: string) => {
    const monthlyBasePrice = baseTier === 'pro' ? 690 : 1590;
    
    const cycles = [
      { 
        value: 'monthly' as const, 
        label: '1 เดือน', 
        tier: `${baseTier}_monthly`,
        months: 1,
        freeMonths: 0,
        basePrice: monthlyBasePrice
      },
      { 
        value: 'semi_annual' as const, 
        label: '6 เดือน', 
        bonus: 'ฟรี 1 เดือน', 
        tier: `${baseTier}_semi_annual`,
        months: 6,
        freeMonths: 1,
        basePrice: monthlyBasePrice
      },
      { 
        value: 'yearly' as const, 
        label: '1 ปี', 
        bonus: 'ฟรี 2 เดือน', 
        tier: `${baseTier}_yearly`,
        months: 12,
        freeMonths: 2,
        basePrice: monthlyBasePrice
      }
    ];
    return cycles.filter(c => SUBSCRIPTION_PLANS.some(p => p.tier === c.tier));
  };

  const getHelperMessage = () => {
    if (isFreeUser) {
      return 'กรุณาเลือกแพ็คเก็จที่ต้องการเพื่อใช้งานต่อเนื่องค่ะ';
    }
    if (tier && tier.includes('pro_plus')) {
      return 'กรุณาต่ออายุเพื่อใช้งานต่อเนื่องค่ะ';
    }
    return 'กรุณาต่ออายุหรืออัพเกรดเพื่อใช้งานต่อเนื่องค่ะ';
  };

  const handleProceedToPayment = () => {
    // Prepare URL addon data for renewal
    const urlAddons = [];
    
    // If user wants to renew existing URL addon
    if (renewUrlAddon && hasUrlAddon && existingUrlAddonType && existingUrlAddonUsername) {
      urlAddons.push({ 
        type: existingUrlAddonType, 
        username: existingUrlAddonUsername,
        isRenewal: true
      });
    }
    
    // If Pro+ Yearly and no existing URL addon, include free Subdomain
    const isProPlusYearly = selectedPlan === 'pro_plus_yearly';
    if (isProPlusYearly && !hasUrlAddon) {
      // Will be handled in checkout - free Subdomain for Pro+ Yearly
      urlAddons.push({ 
        type: 'subdomain', 
        username: '', // Will be set in checkout
        isFree: true,
        isNew: true
      });
    }
    
    // Check if user selected a new plan (upgrade)
    const hasNewPlan = selectedPlan !== tier;
    let selectedPlanData = hasNewPlan ? getPlanByTier(selectedPlan) : plan;
    
    // Fallback: If plan is null, try to get from tier
    if (!selectedPlanData && tier) {
      selectedPlanData = getPlanByTier(tier);
    }
    
    // Last resort: Create a Free Trial plan object
    if (!selectedPlanData) {
      selectedPlanData = {
        tier: 'free',
        name: 'Free Trial (14 วัน)',
        price: 0,
        billing_cycle: 'monthly',
        features: {
          storage_gb: 20,
          unlimited_students: true,
          unlimited_courses: true,
          dashboard_analytics: false,
          seo_optimization: false,
          sale_page_builder: false
        }
      };
    }
    
    const checkoutData: any = {
      plan: hasNewPlan ? selectedPlan : (tier || 'free'),
      planName: selectedPlanData?.name || 'Free Trial (14 วัน)',
      planPrice: selectedPlanData?.price || 0,
      storageAddon: addonStorageGB > 0 ? addonStorageGB : undefined,
      urlAddons: urlAddons.length > 0 ? urlAddons : undefined,
      total: calculateTotal(),
      isRenewal: true,
      isStorageOnly: !hasNewPlan // Only storage if no new plan selected
    };
    
    console.log('💾 [RenewSubscription] Saving to localStorage:', checkoutData);
    localStorage.setItem('subscriptionCheckout', JSON.stringify(checkoutData));
    
    // Navigate with tier parameter as backup
    const tierParam = hasNewPlan ? selectedPlan : (tier || 'free');
    console.log('🚀 [RenewSubscription] Navigating to checkout with tier:', tierParam);
    navigate(`/subscription/checkout-new?tier=${tierParam}`);
  };

  const handleRemoveFromSummary = (addonId: string) => {
    setSelectedAddons(selectedAddons.filter(a => a.id !== addonId));
  };

  const handleEditSelection = () => {
    setShowSummary(false);
  };

  const getCurrentPackageTotal = () => {
    // ใช้ยอดชำระจริงจากประวัติการต่ออายุล่าสุด
    if (subscriptionHistory.length > 0 && subscriptionHistory[0].amount) {
      return subscriptionHistory[0].amount;
    }
    
    // Fallback: คำนวณจากราคาแพ็คเก็จปัจจุบัน (กรณีไม่มีประวัติ)
    const planPrice = plan?.price || 0;
    const addonsPrice = Array.isArray(currentAddons) 
      ? currentAddons.reduce((sum, addon) => sum + (addon.price || 0) * (addon.quantity || 1), 0)
      : 0;
    return planPrice + addonsPrice;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-8 h-8 text-cyan-600" />
            ต่ออายุ Subscription
          </h1>
          <p className="text-gray-600 mt-2">ต่ออายุแพ็คเก็จปัจจุบัน หรือเลือกแพ็คเก็จใหม่</p>
        </div>

        {/* Main Grid Layout - 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">

          {/* Current Package with Quick Renew */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-cyan-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-600" />
              แพ็คเก็จปัจจุบัน
            </h2>
            
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 mb-4">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">แพ็คเกจ</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xl font-bold text-gray-900">{plan?.name || 'FREE'}</p>
                    {daysLeft !== null && daysLeft <= 0 && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-red-600 to-rose-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                        <AlertCircle className="w-3.5 h-3.5" />
                        หมดอายุแล้ว
                      </span>
                    )}
                    {daysLeft !== null && daysLeft > 0 && daysLeft <= 7 && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
                        <AlertCircle className="w-3.5 h-3.5" />
                        ใกล้หมดอายุ
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {isFreeUser ? 'แพ็คเกจฟรี' : `${(limitGB || 0).toFixed(0)} GB`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">ราคา</p>
                  <p className="text-2xl font-bold text-cyan-600">
                    {isFreeUser ? 'ฟรี' : `฿${(plan?.price || 0).toLocaleString()}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{plan?.period || ''}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">วันที่สมัคร</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDate(user?.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">ต่ออายุล่าสุด</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {subscriptionHistory.length > 0 
                      ? formatDate(subscriptionHistory[0].renewedAt) 
                      : (subscriptionData?.lastRenewedAt ? formatDate(subscriptionData.lastRenewedAt) : '-')
                    }
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">หมดอายุ</p>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatDate(expiryDate)}
                      {user?.trialStartedAt && !subscriptionData?.endDate && !user?.subscriptionEndDate && (user?.subscriptionTier === 'free' || user?.plan === 'free') && (
                        <span className="text-xs text-gray-500 ml-1">(Trial)</span>
                      )}
                    </p>
                    {daysLeft !== null && (
                      <p className={`text-xs font-bold ${
                        daysLeft <= 0 
                          ? 'text-red-600' 
                          : daysLeft <= 3 
                          ? 'text-orange-600' 
                          : daysLeft <= 7 
                          ? 'text-amber-600' 
                          : 'text-gray-600'
                      }`}>
                        {daysLeft <= 0 
                          ? `หมดอายุแล้ว ${Math.abs(daysLeft)} วัน` 
                          : `เหลืออีก ${daysLeft} วัน`
                        }
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="text-xs text-cyan-600 hover:text-cyan-700 underline flex items-center gap-1"
                  >
                    <Calendar className="w-3 h-3" />
                    ดูประวัติการต่ออายุ
                  </button>
                </div>
              </div>

              {/* Subscription History */}
              {showHistory && (
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      ประวัติการต่ออายุ
                    </h3>
                    <button
                      onClick={() => setShowHistory(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {subscriptionHistory.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {subscriptionHistory.map((record) => (
                        <div key={record.id} className="bg-gray-50 rounded p-3 text-xs">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-semibold text-gray-900">
                              {record.isUrlAddon ? 'ต่ออายุ URL Add-on' : `ต่ออายุแพ็คเก็จ ${record.tier?.toUpperCase() || ''}`}
                            </span>
                            <span className="text-gray-500">{formatDate(record.renewedAt)}</span>
                          </div>
                          {record.newEndDate && (
                            <div className="text-gray-600">
                              {record.oldEndDate 
                                ? `ระยะเวลา: ${formatDate(record.oldEndDate)} → ${formatDate(record.newEndDate)}`
                                : `หมดอายุ: ${formatDate(record.newEndDate)}`
                              }
                            </div>
                          )}
                          {record.amount !== undefined && (
                            <div className="text-cyan-600 font-semibold mt-1">
                              ยอดชำระ: ฿{record.amount.toLocaleString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">ยังไม่มีประวัติการต่ออายุ</p>
                  )}
                </div>
              )}

              {/* Current Add-ons */}
              {Array.isArray(currentAddons) && currentAddons.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Add-ons ปัจจุบัน</p>
                  {currentAddons.map((addon, index) => (
                    <div key={index} className="flex items-center justify-between text-sm py-1">
                      <span className="text-gray-700">{addon.name}</span>
                      <span className="font-semibold text-gray-900">+{addon.storageGB}GB</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Total for current package */}
              {tier !== 'free' && (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">ราคารวม</span>
                    <span className="text-xl font-bold text-cyan-600">
                      ฿{getCurrentPackageTotal().toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Status Alert */}
            {daysLeft !== null && daysLeft <= 7 && !isFreeUser && (
              <div className={`${daysLeft <= 3 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 mb-4`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 ${daysLeft <= 3 ? 'text-red-600' : 'text-amber-600'} mt-0.5`} />
                  <div>
                    <p className={`font-semibold ${daysLeft <= 3 ? 'text-red-900' : 'text-amber-900'}`}>
                      {daysLeft <= 0 ? 'หมดอายุแล้ว' : `หมดอายุใน ${daysLeft} วัน`}
                    </p>
                    <p className={`text-sm ${daysLeft <= 3 ? 'text-red-700' : 'text-amber-700'} mt-1`}>
                      กรุณาต่ออายุเพื่อใช้งานต่อ
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Helper Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800 text-center">{getHelperMessage()}</p>
            </div>

            {/* Quick Renew or Upgrade Button */}
            {isFreeUser ? (
              <button
                onClick={handleUpgrade}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                <ArrowRight className="w-5 h-5" />
                อัพเกรด
              </button>
            ) : (
              <button
                onClick={handleQuickRenew}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                <RefreshCw className="w-5 h-5" />
                ต่ออายุแพ็คเก็จเดิม
              </button>
            )}
          </div>

          {/* Upgrade Package - Collapsible (Only show if not Pro+ already) */}
          {!tier?.includes('pro_plus') && (
          <div id="upgrade-section" className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-200">
            <button
              onClick={() => setShowUpgradeOptions(!showUpgradeOptions)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">เปลี่ยนแพ็คเก็จใหม่ (อัพเกรด)</h2>
              </div>
              {showUpgradeOptions ? (
                <ChevronUp className="w-6 h-6 text-gray-600" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-600" />
              )}
            </button>

            {showUpgradeOptions && (
              <div className="px-6 pb-6 space-y-4">
                {/* Step 1: Select Package Tier */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">เลือกแพ็คเก็จ</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['pro', 'pro_plus'].filter(baseTier => {
                      const currentBase = (tier || 'free').replace(/_monthly|_semi_annual|_yearly/, '');
                      const hierarchy = ['free', 'pro', 'pro_plus'];
                      // Allow current tier and higher (>= instead of >)
                      return hierarchy.indexOf(baseTier) >= hierarchy.indexOf(currentBase);
                    }).map((baseTier) => {
                      const displayName = baseTier === 'pro' ? 'Pro' : 'Pro+';
                      const baseStorage = baseTier === 'pro' ? 20 : 30;
                      const isSelected = selectedPackageTier === baseTier;
                      
                      // Package benefits
                      const benefits =
                        baseTier === 'pro'
                          ? [
                            { text: 'เริ่มต้น 20 GB', special: false },
                            { text: 'รายปี: 25 GB (โบนัส +5GB)', special: false },
                          ]
                        : [
                            { text: 'เริ่มต้น 30 GB', special: false },
                            { text: 'รายปี: 35 GB (โบนัส +5GB)', special: false },
                            { text: 'รายปี: เซลเพจ 1 หน้าฟรี 1 ปี', special: true, badge: 'ฟรี!', color: 'orange' },
                            { text: 'รายปี: Subdomain ฟรี', special: true, badge: 'ฟรี!', color: 'emerald' },
                          ];
                      
                      return (
                        <button
                          key={baseTier}
                          onClick={() => {
                            if (isSelected) {
                              // Toggle off - deselect completely
                              setSelectedPackageTier(null);
                              setSelectedPlan(tier);
                              setSelectedBillingCycle('monthly');
                              setSelectedSubdomain(false);
                              setSelectedPathUrl(false);
                            } else {
                              // Select this package
                              setSelectedPackageTier(baseTier as 'pro' | 'pro_plus');
                              setSelectedPlan(`${baseTier}_monthly`);
                              setSelectedBillingCycle('monthly');
                              // Auto-select Subdomain for Pro+ Yearly (first-time buyers only)
                              if (`${baseTier}_monthly` === 'pro_plus_yearly' && !hasUrlAddon) {
                                setSelectedSubdomain(true);
                                setSelectedPathUrl(false);
                              }
                            }
                          }}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            isSelected
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-gray-900">{displayName}</h3>
                            {isSelected && <CheckCircle className="w-5 h-5 text-purple-600" />}
                          </div>
                          <ul className="text-xs text-gray-600 space-y-1.5">
                            {benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-1.5">
                                <span className="text-purple-600 mt-0.5">•</span>
                                <div className="flex-1 flex items-center gap-2 flex-wrap">
                                  <span className={benefit.special ? 'font-semibold text-gray-800' : ''}>
                                    {benefit.text}
                                  </span>
                                  {benefit.special && benefit.badge && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                      benefit.color === 'orange' 
                                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm' 
                                        : 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-sm'
                                    }`}>
                                      <Sparkles className="w-3 h-3" />
                                      {benefit.badge}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 2: Select Billing Cycle - Cards */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">เลือกรอบบิล</label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {getBillingCycleOptions(selectedPackageTier || 'pro').map((cycle) => {
                      const cycleplan = getPlanByTier(cycle.tier);
                      if (!cycleplan) return null;
                      
                      const originalPrice = cycle.basePrice * cycle.months;
                      const actualPrice = cycleplan.price;
                      const discount = cycle.basePrice * cycle.freeMonths;
                      const isSelected = selectedBillingCycle === cycle.value;
                      
                      return (
                        <button
                          key={cycle.value}
                          type="button"
                          onClick={() => {
                            setSelectedBillingCycle(cycle.value);
                            const newPlan = `${selectedPackageTier || 'pro'}_${cycle.value}`;
                            setSelectedPlan(newPlan);
                            if (newPlan === 'pro_plus_yearly' && !hasUrlAddon) {
                              setSelectedSubdomain(true);
                              setSelectedPathUrl(false);
                            }
                          }}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            isSelected
                              ? 'border-purple-500 bg-purple-50 shadow-md'
                              : 'border-gray-200 hover:border-purple-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-gray-900 text-lg">{cycle.label}</span>
                                {cycle.bonus && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-rose-500 text-white">
                                    {cycle.bonus}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                {cycle.months} เดือน
                              </div>
                            </div>
                            {isSelected && <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0" />}
                          </div>
                          
                          <div className="space-y-1">
                            {discount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">ราคาปกติ:</span>
                                <span className="text-gray-400 line-through">฿{originalPrice.toLocaleString()}</span>
                              </div>
                            )}
                            {discount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-red-600 font-semibold">ประหยัด:</span>
                                <span className="text-red-600 font-semibold">-฿{discount.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                              <span className="text-sm font-semibold text-gray-700">ราคารวม:</span>
                              <span className="text-2xl font-bold text-purple-600">฿{actualPrice.toLocaleString()}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* URL Add-on Renewal - Only for existing URL addon users */}
          {hasUrlAddon && existingUrlAddonType && existingUrlAddonUsername && (
            <div id="url-addon-section" className="bg-white rounded-2xl shadow-lg p-6 border-2 border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <Package className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">URL Add-on</h2>
              </div>

              {/* Pro+ Yearly Free Subdomain Notice */}
              {selectedPlan === 'pro_plus_yearly' && existingUrlAddonType === 'subdomain' && (
                  <div className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-purple-900 mb-1 flex items-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          Pro+ รายปี รับ Subdomain ฟรี!
                        </p>
                        <p className="text-sm text-purple-700">
                          Subdomain ของคุณจะต่ออายุฟรีอัตโนมัติเมื่อเลือก Pro+ รายปี (มูลค่า ฿990)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Checkbox for URL Add-on Renewal */}
              <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={renewUrlAddon}
                    onChange={(e) => setRenewUrlAddon(e.target.checked)}
                    className="mt-1 w-5 h-5 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        ต่ออายุ URL Add-on ด้วย
                      </span>
                      {selectedPlan === 'pro_plus_yearly' && existingUrlAddonType === 'subdomain' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm">
                          <Sparkles className="w-3 h-3" />
                          ฟรี!
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 space-y-1">
                      <p>
                        <span className="font-medium">ประเภท:</span>{' '}
                        {existingUrlAddonType === 'path' ? 'Path URL' : 'Subdomain'}
                      </p>
                      <p>
                        <span className="font-medium">URL:</span>{' '}
                        <span className="text-purple-600 font-semibold">
                          {existingUrlAddonType === 'path' 
                            ? `edumi.co/${existingUrlAddonUsername}`
                            : `${existingUrlAddonUsername}.edumi.co`
                          }
                        </span>
                      </p>
                      {(() => {
                        const isYearly = selectedPlan.includes('yearly');
                        const isSemiAnnual = selectedPlan.includes('semi_annual');
                        const isProPlusYearly = selectedPlan === 'pro_plus_yearly';
                        
                        let price = 0;
                        if (existingUrlAddonType === 'path') {
                          price = isYearly ? 490 : isSemiAnnual ? 245 : 49;
                        } else if (existingUrlAddonType === 'subdomain') {
                          if (!isProPlusYearly) {
                            price = isYearly ? 990 : isSemiAnnual ? 495 : 99;
                          }
                        }
                        
                        return (
                          <p>
                            <span className="font-medium">ราคา:</span>{' '}
                            {price > 0 ? (
                              <span className="text-purple-600 font-bold">฿{price.toLocaleString()}</span>
                            ) : (
                              <span className="text-purple-600 font-bold">ฟรี!</span>
                            )}
                            <span className="text-gray-500 text-xs ml-1">
                              ({isYearly ? 'ต่อปี' : isSemiAnnual ? '6 เดือน' : 'ต่อเดือน'})
                            </span>
                          </p>
                        );
                      })()}
                    </div>
                    {!renewUrlAddon && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        หากไม่เลือก URL Add-on จะหมดอายุและไม่สามารถใช้งานได้
                      </p>
                    )}
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Pro+ Yearly Free Subdomain Notice - For first-time buyers */}
          {selectedPlan === 'pro_plus_yearly' && !hasUrlAddon && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-lg p-6 border-2 border-purple-300">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-purple-900 mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Pro+ รายปี รับ Subdomain ฟรี!
                  </h3>
                  <p className="text-sm text-purple-700 mb-2">
                    เมื่อคุณเลือก Pro+ รายปี คุณจะได้รับ Subdomain ฟรี 1 ปี (มูลค่า ฿990)
                  </p>
                  <p className="text-xs text-purple-600 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" />
                    คุณสามารถตั้งค่า Username สำหรับ Subdomain ได้ในหน้า Checkout
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Duplicate section removed - keeping only the correct one below */}

          {/* Add Storage - HIDDEN: Feature removed */}
          {false && !isFreeUser && !(isFreeUser && !hasEverHadPaidSubscription) && (
            <div id="storage-addon-section" className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-200">
              <button
                onClick={() => setShowAddonOptions(!showAddonOptions)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <HardDrive className="w-5 h-5 text-orange-600" />
                  <h2 className="text-xl font-bold text-gray-900">เพิ่มพื้นที่จัดเก็บ (Add-on)</h2>
                </div>
                {showAddonOptions ? (
                  <ChevronUp className="w-6 h-6 text-gray-600" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-600" />
                )}
              </button>

              {showAddonOptions && (
                <div className="px-6 pb-6">
                  <p className="text-sm text-gray-600 mb-4">เลือกพื้นที่เพิ่มเติมที่ต้องการซื้อ (ซื้อครั้งเดียว ไม่ต้องต่ออายุ)</p>
                  
                  {/* Storage Input with +/- buttons */}
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-700">จำนวนพื้นที่ที่ต้องการเพิ่ม</label>
                      {addonStorageGB > 0 && (
                        <button
                          onClick={() => setAddonStorageGB(0)}
                          className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 font-medium"
                        >
                          <X className="w-3 h-3" />
                          ยกเลิก
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 mb-4">
                      <button
                        onClick={() => {
                          const newValue = Math.max(0, addonStorageGB - 5);
                          setAddonStorageGB(newValue);
                        }}
                        disabled={addonStorageGB <= 0}
                        className="w-12 h-12 flex items-center justify-center bg-white border-2 border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <Minus className="w-5 h-5 text-orange-600" />
                      </button>
                      
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={addonStorageGB}
                          onChange={(e) => {
                            const input = e.target.value.replace(/[^0-9]/g, '');
                            const value = input === '' ? 0 : parseInt(input);
                            if (value >= 0 && value <= 500) {
                              setAddonStorageGB(value);
                            }
                          }}
                          onBlur={(e) => {
                            const input = e.target.value.replace(/[^0-9]/g, '');
                            const value = input === '' ? 0 : parseInt(input);
                            const rounded = Math.round(value / 5) * 5;
                            setAddonStorageGB(Math.max(0, Math.min(500, rounded)));
                          }}
                          className="w-full px-4 py-3 text-center text-2xl font-bold border-2 border-orange-300 rounded-lg focus:border-orange-500 focus:outline-none"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-600">GB</span>
                      </div>
                      
                      <button
                        onClick={() => {
                          const newValue = Math.min(500, addonStorageGB + 5);
                          setAddonStorageGB(newValue);
                        }}
                        disabled={addonStorageGB >= 500}
                        className="w-12 h-12 flex items-center justify-center bg-white border-2 border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <Plus className="w-5 h-5 text-orange-600" />
                      </button>
                    </div>

                    {/* Price Calculation */}
                    {addonStorageGB > 0 && (() => {
                      const calculateAddonPrice = (gb: number) => {
                        if (gb <= 0) return 0;
                        
                        // Simple pricing: ฿99 per 5GB
                        // 5GB = ฿99 (ไม่ปัด)
                        // 10GB = ฿198 → ฿190
                        // 15GB = ฿297 → ฿290
                        const units = gb / 5;
                        const rawPrice = units * 99;
                        
                        // ตัดเศษเป็น 0 เฉพาะตั้งแต่ 10GB ขึ้นไป
                        if (gb === 5) return 99;
                        return Math.floor(rawPrice / 10) * 10;
                      };
                      
                      const price = calculateAddonPrice(addonStorageGB);
                      
                      return (
                        <div className="flex items-center justify-between pt-3 border-t-2 border-orange-200">
                          <span className="text-lg font-semibold text-gray-900">ราคารวม:</span>
                          <span className="text-3xl font-bold text-orange-600">฿{price.toLocaleString()}</span>
                        </div>
                      );
                    })()}
                    
                    {addonStorageGB === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        กรุณาเลือกจำนวนพื้นที่ที่ต้องการเพิ่ม (ทีละ 5GB)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          </div>
          {/* End Left Column */}

          {/* Right Column - Sticky Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {(selectedPlan !== tier || selectedAddons.length > 0 || addonStorageGB > 0 || (renewUrlAddon && hasUrlAddon)) ? (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-lg p-6 border-2 border-cyan-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">สรุปรายการ</h3>
                  
                  {/* Total Price */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">ยอดรวมทั้งหมด</p>
                    <p className="text-4xl font-bold text-cyan-600">
                      ฿{calculateTotal().toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedPlan.includes('yearly') ? 'ต่อปี' : selectedPlan.includes('semi_annual') ? '6 เดือน' : 'ต่อเดือน'}
                    </p>
                  </div>

                  {/* Total Storage */}
                  <div className="mb-6 pb-6 border-b border-cyan-200">
                    <p className="text-sm text-gray-600 mb-2">พื้นที่รวม</p>
                    <p className="text-3xl font-bold text-gray-900 mb-3">
                      {calculateTotalStorage().toFixed(0)} GB
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">จากแพ็คเก็จ:</span>
                        <span className="font-semibold text-gray-700">
                          {((getPlanByTier(selectedPlan)?.features.storageGB.videoStorage || 0) / 1024).toFixed(0)} GB
                        </span>
                      </div>
                      {(() => {
                        const currentAddonsStorage = Array.isArray(currentAddons) 
                          ? currentAddons.reduce((sum, addon) => sum + (addon.storageGB || 0) * (addon.quantity || 1), 0)
                          : 0;
                        const selectedAddonsStorage = selectedAddons.reduce((sum, addon) => sum + (addon.storageGB * addon.quantity), 0);
                        const totalAddons = currentAddonsStorage + selectedAddonsStorage + addonStorageGB;
                        
                        if (totalAddons > 0) {
                          return (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">จาก Add-on Storage:</span>
                              <span className="font-semibold text-orange-600">
                                +{totalAddons.toFixed(0)} GB
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div className="space-y-3 mb-6">
                    {/* Plan */}
                    {selectedPlan !== tier && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">แพ็คเก็จ {getPlanByTier(selectedPlan)?.name}</span>
                        <span className="font-semibold text-gray-900">
                          ฿{(getPlanByTier(selectedPlan)?.price || 0).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* URL Add-on Renewal */}
                    {renewUrlAddon && hasUrlAddon && existingUrlAddonType && (() => {
                      const isYearly = selectedPlan.includes('yearly');
                      const isSemiAnnual = selectedPlan.includes('semi_annual');
                      const isProPlusYearly = selectedPlan === 'pro_plus_yearly';
                      
                      let price = 0;
                      let label = '';
                      
                      if (existingUrlAddonType === 'path') {
                        price = isYearly ? 490 : isSemiAnnual ? 245 : 49;
                        label = 'Path URL';
                      } else if (existingUrlAddonType === 'subdomain') {
                        label = 'Subdomain';
                        if (!isProPlusYearly) {
                          price = isYearly ? 990 : isSemiAnnual ? 495 : 99;
                        }
                      }
                      
                      return (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{label}</span>
                          <span className="font-semibold text-purple-600">
                            {price > 0 ? (
                              `฿${price.toLocaleString()}`
                            ) : (
                              <span className="flex items-center gap-1">
                                <span className="line-through text-gray-400">฿990</span>
                                <span className="text-purple-600 font-bold">ฟรี!</span>
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {/* Free Subdomain for Pro+ Yearly (first-time buyers) */}
                    {selectedPlan === 'pro_plus_yearly' && !hasUrlAddon && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Subdomain</span>
                        <span className="font-semibold flex items-center gap-1">
                          <span className="line-through text-gray-400">฿990</span>
                          <span className="text-purple-600 font-bold">ฟรี!</span>
                        </span>
                      </div>
                    )}
                    
                    {/* Salepage Free for Pro+ Yearly */}
                    {selectedPlan === 'pro_plus_yearly' && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Salepage 1 หน้า (1 ปี)</span>
                        <span className="font-semibold flex items-center gap-1">
                          <span className="line-through text-gray-400">฿1,788</span>
                          <span className="text-purple-600 font-bold">ฟรี!</span>
                        </span>
                      </div>
                    )}

                    {/* Storage Add-on */}
                    {addonStorageGB > 0 && (() => {
                      const calculateAddonPrice = (gb: number) => {
                        if (gb <= 0) return 0;
                        const units = gb / 5;
                        const rawPrice = units * 99;
                        if (gb === 5) return 99;
                        return Math.floor(rawPrice / 10) * 10;
                      };
                      const price = calculateAddonPrice(addonStorageGB);
                      return (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Storage +{addonStorageGB}GB</span>
                          <span className="font-semibold text-orange-600">฿{price.toLocaleString()}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Total Savings for Pro+ Yearly */}
                  {selectedPlan === 'pro_plus_yearly' && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-purple-900 flex items-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          ประหยัดรวม:
                        </span>
                        <span className="text-2xl font-bold text-purple-600">
                          ฿{(990 + 1788).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-purple-700 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>• Subdomain ฟรี</span>
                          <span className="font-semibold">฿990</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>• Salepage 1 หน้า ฟรี</span>
                          <span className="font-semibold">฿1,788</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Proceed Button */}
                  <button
                    onClick={handleProceedToPayment}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    ดำเนินการชำระเงิน
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200">
                  <p className="text-center text-gray-500 text-sm">
                    เลือกแพ็คเก็จหรือ Add-on เพื่อดูสรุปราคา
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* End Right Column */}

        </div>
        {/* End Grid Layout */}
      </div>
    </div>
  );
}
